'use no memo';

import { getTrafficSource, isSessionStorageAllowed } from '@local/utils';
import { v4 as uuidv4 } from 'uuid';
import Cookies from 'universal-cookie';
import { includes, pick, isEqual, isEmpty } from 'lodash-es';

import { useSelectedAddressStore } from 'store';

import { SOCIAL_LOGIN_ERRORS } from 'helpers/api';
import { readCnChannelWithImpliedRestaurantId } from 'helpers/channel';
import {
  EXPERIMENT_COOKIE,
  MEMBERSHIP_STATUS,
  MEMBERSHIP_PLANS,
} from 'helpers/constants';

export function getCnChannelXid() {
  let cnChannelXid = '';

  if (isSessionStorageAllowed()) {
    cnChannelXid = JSON.parse(sessionStorage.getItem('cnChannelXid'));
  }

  return cnChannelXid;
}

export function setCnChannelXid(id) {
  if (isSessionStorageAllowed()) {
    sessionStorage.setItem(
      'cnChannelXid',
      JSON.stringify(id.substring(0, 100)) // cut off any characters if longer than 100
    );
  }
}

export function getUserAuthStatus() {
  let status;

  if (isSessionStorageAllowed()) {
    status = JSON.parse(sessionStorage.getItem('userAuthStatus'));
  }

  return status;
}

export function setUserAuthStatus(bool) {
  if (isSessionStorageAllowed()) {
    sessionStorage.setItem('userAuthStatus', bool);
  }
}

export function getUseHighContrast() {
  if (isSessionStorageAllowed()) {
    return JSON.parse(sessionStorage.getItem('useHighContrast')) === true;
  }

  return false;
}

export function persistUseHighContrast(bool) {
  if (isSessionStorageAllowed()) {
    sessionStorage.setItem('useHighContrast', JSON.stringify(bool));
  }
}

export function getChannelInfoForOrderMeta(restaurantId) {
  // if we don't have a channel, don't set anything as BE requires a channel to accept any other info
  const cnChannel = restaurantId
    ? getTrafficSource(restaurantId)
    : // this function is used to prepare the default redux state so restaurantId is nullable
      // TODO: remove?
      readCnChannelWithImpliedRestaurantId();

  return cnChannel
    ? {
        cn_channel: cnChannel,
        cn_channel_xid: getCnChannelXid(),
      }
    : null;
}

export function getUserExperimentId() {
  const cookies = new Cookies();
  const cookie = cookies.get(EXPERIMENT_COOKIE);
  return cookie;
}

export function getUserSessionId() {
  let userId = isSessionStorageAllowed() && sessionStorage.getItem('userId');

  if (!userId) {
    userId = uuidv4();
    if (isSessionStorageAllowed()) {
      sessionStorage.setItem('userId', userId);
    }
  }

  return userId;
}

export function getMarketingOptInStatusFromSessionStorage() {
  const marketingOptInStatus =
    isSessionStorageAllowed() && sessionStorage.getItem('marketingOptInStatus');
  const JSONmarketingOptInStatus = JSON.parse(marketingOptInStatus);
  return JSONmarketingOptInStatus;
}

export function setMarketingOptInStatusFromSessionStorage({
  restaurantId,
  optInStatus,
}) {
  if (isSessionStorageAllowed()) {
    sessionStorage.setItem(
      'marketingOptInStatus',
      JSON.stringify({ restaurantId, optInStatus })
    );
  }
}

export function clearMarketingOptInStatus() {
  if (isSessionStorageAllowed()) {
    sessionStorage.removeItem('marketingOptInStatus');
  }
}

export function getRewardsOptInStatusFromSessionStorage() {
  const rewardsOptInStatus =
    isSessionStorageAllowed() && sessionStorage.getItem('rewardsOptInStatus');
  const JSONrewardsOptInStatus = JSON.parse(rewardsOptInStatus);
  // If item does not exist yet send true by default
  return isEmpty(JSONrewardsOptInStatus)
    ? { optInStatus: true }
    : JSONrewardsOptInStatus;
}

export function setRewardsOptInStatusFromSessionStorage({
  restaurantId,
  optInStatus,
}) {
  if (isSessionStorageAllowed()) {
    sessionStorage.setItem(
      'rewardsOptInStatus',
      JSON.stringify({ restaurantId, optInStatus })
    );
  }
}

export function getSocialErrors(errors) {
  return (
    (errors.length &&
      errors.filter((error) => includes(SOCIAL_LOGIN_ERRORS, error.code))) ||
    []
  );
}

export function getRemoveSocialErrors(errors) {
  return (
    (errors.length &&
      errors.filter(
        (error) => !includes(SOCIAL_LOGIN_ERRORS, error.code) // all errors other than social errors
      )) ||
    []
  );
}

// Ensure any null/undefined values from DB are cast as strings for comparison against API response
export function convertValuesToStrings(address) {
  const formatted = { ...address };
  if (!formatted.delivery_instructions) {
    formatted.delivery_instructions = '';
  }
  return Object.fromEntries(
    Object.entries(formatted).map(([k, v]) => [k, v ? v.toString() : ''])
  );
}

export async function checkDuplicateAddressesAgainstStorage(addressesFromUser) {
  const keysToCompare = [
    'street_address1',
    'street_address2',
    'country_alpha2',
    'zip',
    'city',
    'state',
    'delivery_instructions',
  ];

  const selectedAddress = useSelectedAddressStore.getState().address;
  if (!selectedAddress) return;
  const sanitizedSelectedAddress = pick(
    convertValuesToStrings(selectedAddress),
    keysToCompare
  );
  const matchedAddress = addressesFromUser.find((addressFromStore) => {
    const sanitzedAddressFromAccount = pick(
      convertValuesToStrings(addressFromStore),
      keysToCompare
    );
    return isEqual(sanitizedSelectedAddress, sanitzedAddressFromAccount);
  });
  if (matchedAddress) {
    useSelectedAddressStore.getState().setAddress(matchedAddress);
  }
}

export function getUserMembershipPlan({ userMemberships, restaurant }) {
  if (!restaurant) return;

  const userMembership = userMemberships.find(
    ({ restaurant_id: restaurantId, is_active: isActive }) =>
      restaurantId === restaurant.id && isActive
  );

  // eslint-disable-next-line camelcase
  const { plan_xid = '', expires_at } = userMembership || {};
  // eslint-disable-next-line camelcase
  const userMembershipPlan = MEMBERSHIP_PLANS.find(({ id }) => plan_xid === id);

  // eslint-disable-next-line camelcase
  return { ...userMembershipPlan, expires_at }; // eslint-disable-line consistent-return
}

export function getHasActiveMembership({ userMemberships, restaurant }) {
  if (!restaurant) return;

  const userMembership = userMemberships.find(
    ({ restaurant_id: restaurantId, is_active: isActive }) =>
      restaurantId === (restaurant && restaurant.id) && isActive
  );

  const hasActiveMembership = typeof userMembership !== 'undefined'; // eslint-disable-line consistent-return

  return hasActiveMembership; // eslint-disable-line consistent-return
}

export function getExpiredMemberships({ userMemberships, restaurant }) {
  if (!restaurant) return;

  const userMembership = userMemberships.filter(
    ({ restaurant_id: restaurantId, status }) =>
      restaurantId === restaurant.id && status === MEMBERSHIP_STATUS.expired
  );
  return userMembership || {}; // eslint-disable-line consistent-return
}
